import React, { useEffect, useContext, useState } from 'react';
import { AuthContext } from '../context/AuthContext';
import Sidebar from '../components/DashboardSidebar';
import { FileText, Video, Lightbulb, Menu, Pencil, MailIcon, BookOpenCheck, Palette, Presentation, CalendarClock, GraduationCap, Library, Activity, LucideCctv, Workflow, Bot, FilePlus, LucideFunnelPlus, LucideRocket, Clapperboard, Megaphone, BotMessageSquare } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { useTranslation } from 'react-i18next';



const iconMap = {
  'smart-doc': <FileText className="w-10 h-10 text-secondary" />,
  'vision-guard': <Video className="w-10 h-10 text-secondary" />,
  'ideabot': <Lightbulb className="w-10 h-10 text-secondary" />,
  'content-creator': <Pencil className="w-10 h-10 text-secondary" />,
  'email-mentor': <MailIcon className="w-10 h-10 text-secondary" />,
  'study-snap': <BookOpenCheck className="w-10 h-10 text-secondary" />,
  'brand-forge': <Palette className="w-10 h-10 text-secondary" />,
  'pitch-craft': <Presentation className="w-10 h-10 text-secondary" />,
  'meet-mate': <CalendarClock className="w-10 h-10 text-secondary" />,
  'skill-sage': <GraduationCap className="w-10 h-10 text-secondary" />,
  'wiki-forge': <Library className="w-10 h-10 text-secondary" />,
  'trend-pulse': <Activity className="w-10 h-10 text-secondary" />,
  'vision-gaurd': <LucideCctv className="w-10 h-10 text-secondary" />,
  'docu-craft': <FilePlus className="w-10 h-10 text-secondary" />,
  'flow-builder': <Workflow className="w-10 h-10 text-secondary" />,
  'meet-genie': <Bot className="w-10 h-10 text-secondary" />,
  'lead-collector': <LucideFunnelPlus className="w-10 h-10 text-secondary" />,
  'script-craft': <Clapperboard className="w-10 h-10 text-secondary" />,
  'ad-genie-salon': <Megaphone className="w-10 h-10 text-secondary" />,
  'replybot': <BotMessageSquare className="w-10 h-10 text-secondary" />,
};



function AppCard({ app, onClick, t }) {
  return (
    <div
      key={app._id}
      onClick={() => onClick(app.routeKey)}
      className="bg-white p-6 rounded shadow hover:scale-105 hover:shadow-lg transition duration-200 cursor-pointer text-center"
    >
      <div className="inline-flex items-center justify-center bg-secondary/10 rounded-full p-4 mb-4">
        {iconMap[app.routeKey] || <FileText className="w-10 h-10 text-secondary" />}
      </div>
      <h3 className="text-lg font-semibold text-gray-800 mb-2">{t(app.name)}</h3>
      {app.status && (
        <span className="inline-block text-xs px-2 py-1 rounded-full bg-primary text-white">
          {t(app.status)}
        </span>
      )}
    </div>
  );
}

function AppsGrid() {
  const [apps, setApps] = useState([]);
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    fetchWithAuth(`${process.env.REACT_APP_API_BASE}/api/users/available-apps`)
      .then(async (res) => {
        if (res.status === 401 || res.status === 403) {
          const data = await res.json();
          localStorage.setItem('redirectMessage', data.message);
          window.location.href = '/login';
          return;
        }

        const data = await res.json();
        if (Array.isArray(data)) setApps(data);
        else setApps([]);
      })
      .catch(console.error);
  }, []);

  const categorized = {
    hot: apps.filter((a) => a.status === 'Hot'),
    new: apps.filter((a) => a.status === 'New'),
    beta: apps.filter((a) => a.status === 'Beta'),
    coming: apps.filter((a) => a.status === 'Coming Soon'),
  };

  const renderSection = (label, list) => (
    list.length > 0 && (
      <div className="mb-10">
        <h2 className="text-xl font-semibold mb-4 text-gray-700">{t(label)}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {list.map(app => (
            <AppCard key={app._id} app={app} onClick={(key) => navigate(`/app/${key}`)} t={t} />
          ))}
        </div>
      </div>
    )
  );

  return (
    <div>
      
      {renderSection(t('hot_apps'), categorized.hot)}
      {renderSection(t('new_apps'), categorized.new)}
      {renderSection(t('beta_apps'), categorized.beta)}
      {renderSection(t('coming_soon'), categorized.coming)}
      {apps.length === 0 && (
        <p className="text-gray-500 text-sm text-center">{t('no_apps_message')}</p>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useContext(AuthContext);
  const [lang, setLang] = useState(user.language || 'en');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await fetchWithAuth(`${process.env.REACT_APP_API_BASE}/api/users/me`);
        if (!res.ok) {console.log(res);
          navigate('/login');
          return;
        }
  
        const fullUser = await res.json();
  
        if (fullUser.firstLoginCompleted === false) {
          navigate('/selectindustry');
        }
      } catch (err) {
        console.error('Failed to fetch user', err);
        navigate('/login');
      }
    };
  
    fetchUser();
  }, [navigate]);
  

  useEffect(() => {
    document.title = "Dashboard – Ennoverse";
  }, []);

  return (
    <div className="flex min-h-screen bg-gray-100">
      {/* Sidebar - fixed on mobile, static on desktop */}
      <div className={`${sidebarOpen ? 'block' : 'hidden'} fixed md:relative md:block z-40 w-64`}>
        <Sidebar
          currentLang={lang}
          onLangChange={setLang}
          onCloseMobile={() => setSidebarOpen(false)}
        />
      </div>

      {/* Main content - no margin */}
      <div className="flex-1 p-6 bg-gray-100 min-h-screen">
        {/* Hamburger for mobile */}
        <button onClick={() => setSidebarOpen(true)} className="block md:hidden mb-4">
          <Menu className="w-6 h-6 text-gray-700" />
        </button>

        <h2 className="text-3xl font-semibold mb-4 text-gray-800">
          {t('dashboard')} <a
  href="https://pitch.com/v/ennoverse-apps-preview-c7tvw3"
  target="_blank"
  rel="noopener noreferrer"
  className="inline-flex items-center mb-4 text-sm font-semibold rounded-full px-4 py-2 bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 transition"
>
<LucideRocket className="w-5 h-5 text-secondary" />&nbsp; {t('upcoming_apps_preview')}
</a>
        </h2>
        
        <div>
          <AppsGrid />
        </div>
      </div>
    </div>
  );
}
